import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SingleSelect from "@/pages/components/single-slect";
import {
  useHcbbAdvancedFilters,
  useHcbbResultView,
} from "@/hooks/use-hcbb-cost-estimator";
import {
  computeHcbbFilterOptions,
  filterHcbbNameSuggestions,
  resolveHcbbActiveTab,
  toHcbbFacilitySuggestions,
  toHcbbProfessionalSuggestions,
} from "@/lib/hcbb-cost-estimator-utils";
import type {
  HcbbFilterOptions,
  HcbbNameSuggestion,
} from "@/lib/hcbb-cost-estimator-utils";
import { INITIAL_HCBB_ADVANCED_FILTER_VALUES } from "@/store/hcbb-cost-estimator/hcbb-advanced-filters-slice";
import type { HcbbAdvancedFilters } from "@/store/hcbb-cost-estimator/hcbb-advanced-filters-slice";
import { INITIAL_HCBB_RESULT_VIEW } from "@/store/hcbb-cost-estimator/hcbb-result-view-slice";
import type { HcbbResultTab } from "@/store/hcbb-cost-estimator/hcbb-result-view-slice";
import type {
  HcbbFacilityResult,
  HcbbProfessionalResult,
} from "@/model/hcbb-cost-estimator";
import type { OptionType } from "@/types";

type HcbbAdvancedSearchFiltersPanelProps = {
  resultKey: string;
  facilities: HcbbFacilityResult[];
  professionals: HcbbProfessionalResult[];
};

/**
 * Wires the panel's Redux state and the data it filters together.
 *
 * Options and suggestions read straight off the full `/estimate` lists —
 * both already carry every candidate's `specialty`/`networkName`/
 * `facilityType`/`npi` up front, so unlike before this change, nothing here
 * depends on a professional batch having been fetched via View More.
 */
export const HcbbAdvancedSearchFiltersPanel = ({
  resultKey,
  facilities,
  professionals,
}: HcbbAdvancedSearchFiltersPanelProps) => {
  const view = useHcbbResultView(resultKey);
  const filters = useHcbbAdvancedFilters(resultKey);

  const activeTab = resolveHcbbActiveTab(view.activeTab, facilities);
  const options = useMemo(
    () => computeHcbbFilterOptions(facilities, professionals),
    [facilities, professionals],
  );
  const facilitySuggestions = useMemo(
    () => toHcbbFacilitySuggestions(facilities),
    [facilities],
  );
  const physicianSuggestions = useMemo(
    () => toHcbbProfessionalSuggestions(professionals),
    [professionals],
  );

  // The filtered candidate list's own reveal depth starts over whenever the
  // criteria change — see the identical reset in hcbb-search-results.tsx's
  // chip-removal/Clear-All handlers.
  const resetFilteredDepth = () =>
    view.setFilteredVisibleBatches(
      INITIAL_HCBB_RESULT_VIEW.filteredVisibleBatches,
    );

  return (
    <HcbbAdvancedSearchFilters
      activeTab={activeTab}
      options={options}
      facilitySuggestions={facilitySuggestions}
      physicianSuggestions={physicianSuggestions}
      committed={filters.filters}
      onApply={(next) => {
        filters.setFilters(next);
        filters.setPanelOpen(false);
        resetFilteredDepth();
      }}
      onClear={() => {
        filters.clearFilters();
        resetFilteredDepth();
      }}
      onClose={() => filters.setPanelOpen(false)}
    />
  );
};

type HcbbAdvancedSearchFiltersProps = {
  activeTab: HcbbResultTab;
  options: HcbbFilterOptions;
  facilitySuggestions: HcbbNameSuggestion[];
  physicianSuggestions: HcbbNameSuggestion[];
  committed: HcbbAdvancedFilters;
  onApply: (filters: HcbbAdvancedFilters) => void;
  onClear: () => void;
  /** Closes the panel without applying the draft — X, Cancel, or Escape. */
  onClose: () => void;
};

const findOption = (
  list: OptionType[],
  value: string | null,
): OptionType | undefined =>
  value ? list.find((option) => option.value === value) : undefined;

/** How many type-ahead matches to show at once under a name/ID field. */
const MAX_NAME_SUGGESTIONS = 8;

/**
 * A free-text field with a type-ahead suggestion list drawn from the rows
 * already on screen — no request of its own. Picking a suggestion fills the
 * field with that row's exact name, which `applyHcbbFacilityFilters`/
 * `applyHcbbProfessionalFilters` then substring-match against on Apply, the
 * same as anything hand-typed.
 *
 * The list itself is portaled to `document.body` and positioned from the
 * input's own bounding rect, rather than sitting `absolute` inside this
 * field's own markup. This panel opens with a height-reveal animation
 * (`HcbbSearchForm`) that briefly clips overflowing content while it plays —
 * a plain nested `absolute` dropdown would be invisible (present in the DOM,
 * clipped from view) for anyone who opens the panel and starts typing before
 * that finishes. Escaping via a portal makes that ordering irrelevant.
 */
const HcbbNameSuggestField = ({
  id,
  label,
  placeholder,
  value,
  suggestions,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  suggestions: HcbbNameSuggestion[];
  onChange: (value: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = `${id}-listbox`;

  // Nothing to type ahead against — disable rather than leave a text field
  // that looks live but can never suggest or usefully filter anything.
  const isDisabled = suggestions.length === 0;

  const matches = filterHcbbNameSuggestions(suggestions, value).slice(
    0,
    MAX_NAME_SUGGESTIONS,
  );
  const showSuggestions = isOpen && matches.length > 0;

  // Tracks the input's own position rather than relying on CSS to place the
  // list, since the portal below detaches it from this field's DOM subtree.
  useEffect(() => {
    if (!showSuggestions) return;

    const updatePosition = () => {
      const rect = fieldRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Viewport-relative, to match the portal's `position: fixed` below —
      // not document-relative, so no scroll offset added here.
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [showSuggestions]);

  // Not `useDismissOnOutside`: the portaled list is a DOM child of
  // `document.body`, not of `fieldRef`, so a single containment check would
  // read every click on a suggestion as "outside" and close the list before
  // its own onClick ever ran.
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (fieldRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center rounded-md border px-2 has-[:disabled]:opacity-50">
        <div ref={fieldRef} className="flex flex-1 items-center">
          <Search className="mr-2 size-4 opacity-50" aria-hidden="true" />
          <Input
            id={id}
            placeholder={
              isDisabled ? "None on file for this search" : placeholder
            }
            value={value}
            disabled={isDisabled}
            autoComplete="off"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-controls={listboxId}
            onFocus={() => setIsOpen(true)}
            onChange={(event) => {
              onChange(event.target.value);
              setIsOpen(true);
            }}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
      </div>

      {showSuggestions &&
        position &&
        createPortal(
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={`${label} suggestions`}
            className="fixed z-50 max-h-60 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
            }}
          >
            {matches.map((match) => (
              <button
                key={match.id}
                type="button"
                role="option"
                aria-selected={match.name === value}
                onClick={() => {
                  onChange(match.name);
                  setIsOpen(false);
                }}
                className="flex w-full flex-col items-start px-3 py-1.5 text-left text-sm hover:bg-primary-extraLight"
              >
                <span className="font-semibold text-gray-900">
                  {match.name}
                </span>
                <span className="text-xs text-gray-500">NPI: {match.npi}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

/**
 * The presentational filter form.
 *
 * `draft` is local and only reaches Redux on Apply. Closing this panel by any
 * route (X, Cancel, Escape, or a backdrop-free unmount) discards the draft
 * for free, since `HcbbSearchForm` only mounts it while open — so a later
 * open always starts from a fresh `useState(committed)`.
 *
 * `committed` can still change while this panel stays mounted, though: the
 * applied-filter chips (`HcbbActiveFilters`, a sibling rendered by
 * `HcbbSearchResults`) remove a field or clear all of them directly, without
 * going through this panel's Apply/Cancel. Left alone, the draft would keep
 * showing values the member just cleared from outside the panel — the effect
 * below re-seeds it whenever `committed` changes after mount.
 *
 * Not built on `@tanstack/react-form`: `HcbbSearchForm`, the reference form
 * for this exact page, is plain controlled inputs + Redux dispatch too, and
 * there is no validation need here — every field is either an enum picked
 * from a dropdown or free text with no format constraint.
 *
 * Rendered inline, nested directly inside the search card's own container
 * (see `HcbbSearchForm`) rather than in a `RightSidebar` slide-over — there
 * is no backdrop, so Escape is the only non-click way to dismiss it and is
 * wired up here.
 */
export const HcbbAdvancedSearchFilters = ({
  activeTab,
  options,
  facilitySuggestions,
  physicianSuggestions,
  committed,
  onApply,
  onClear,
  onClose,
}: HcbbAdvancedSearchFiltersProps) => {
  const [draft, setDraft] = useState<HcbbAdvancedFilters>(committed);
  // `SingleSelect` keeps its displayed value in its own internal state once a
  // member picks one, rather than staying fully controlled by `selectedOption`
  // — so clearing `draft` alone would leave the old label showing. Bumping
  // this key remounts the three dropdowns below, which is the reliable way to
  // force their display back to the placeholder.
  const [resetGeneration, setResetGeneration] = useState(0);
  const isFacilities = activeTab === "facilities";
  const isFirstCommittedRef = useRef(true);

  // See the doc comment above: a chip outside this panel can change
  // `committed` while the panel is still open. `committed` is a stable Redux
  // reference otherwise, so this only fires on a genuine external change, not
  // on every render — skipping the first run avoids immediately bumping
  // `resetGeneration` for the seed this component already mounted with.
  useEffect(() => {
    if (isFirstCommittedRef.current) {
      isFirstCommittedRef.current = false;
      return;
    }
    setDraft(committed);
    setResetGeneration((generation) => generation + 1);
  }, [committed]);

  const networkOptions = isFacilities
    ? options.facilityNetworkNames
    : options.professionalNetworkNames;
  const facilityTypeOptions = isFacilities
    ? options.facilityTypes
    : options.professionalFacilityTypes;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Clear All commits the empty state immediately (unlike Cancel/Escape,
  // which discard the draft) and closes the panel, the same as every other
  // action in this footer/header — nothing here leaves the panel open.
  const handleClearAll = () => {
    setDraft(INITIAL_HCBB_ADVANCED_FILTER_VALUES);
    setResetGeneration((generation) => generation + 1);
    onClear();
    onClose();
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-3 border-b p-4">
        <h3 className="text-base font-semibold text-gray-900">
          Advanced Filter
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Close advanced filters"
          onClick={onClose}
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="space-y-5 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Network Name</Label>
            <SingleSelect
              key={`network-${resetGeneration}`}
              // Nothing to pick from, on this tab's candidates — disabled
              // rather than left as a dropdown that opens onto an empty
              // list, the same reasoning `HcbbNameSuggestField` already
              // applies to the name/NPI fields below.
              placeholder={
                networkOptions.length === 0
                  ? "None on file for this search"
                  : "Select Network Name"
              }
              disabled={networkOptions.length === 0}
              searchEnabled={false}
              data={networkOptions}
              selectedOption={findOption(networkOptions, draft.networkName)}
              onSelect={(item) =>
                setDraft((prev) => ({
                  ...prev,
                  networkName: String(item.value),
                }))
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label>Provider Specialty</Label>
            <SingleSelect
              key={`specialty-${resetGeneration}`}
              placeholder={
                options.professionalSpecialties.length === 0
                  ? "None on file for this search"
                  : "Select Specialty"
              }
              disabled={options.professionalSpecialties.length === 0}
              searchEnabled={false}
              data={options.professionalSpecialties}
              selectedOption={findOption(
                options.professionalSpecialties,
                draft.specialty,
              )}
              onSelect={(item) =>
                setDraft((prev) => ({
                  ...prev,
                  specialty: String(item.value),
                }))
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label>Facility Type</Label>
            <SingleSelect
              key={`facility-type-${resetGeneration}`}
              placeholder={
                facilityTypeOptions.length === 0
                  ? "None on file for this search"
                  : "Select Facility"
              }
              disabled={facilityTypeOptions.length === 0}
              searchEnabled={false}
              data={facilityTypeOptions}
              selectedOption={findOption(
                facilityTypeOptions,
                draft.facilityType,
              )}
              onSelect={(item) =>
                setDraft((prev) => ({
                  ...prev,
                  facilityType: String(item.value),
                }))
              }
            />
          </div>
        </div>

        <div className="border-t" />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <HcbbNameSuggestField
            id="hcbb-filter-facility-query"
            label="Facility Name or NPI"
            placeholder="Enter Facility Name or NPI"
            value={draft.facilityQuery}
            suggestions={facilitySuggestions}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, facilityQuery: value }))
            }
          />

          <HcbbNameSuggestField
            id="hcbb-filter-physician-query"
            label="Physician Name or NPI"
            placeholder="Enter Physician Name or NPI"
            value={draft.physicianQuery}
            suggestions={physicianSuggestions}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, physicianQuery: value }))
            }
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-b-lg border-t bg-gray-50 p-4">
        <Button type="button" variant="link" onClick={handleClearAll}>
          Clear All
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={() =>
              // Trimmed on commit, not just at match time: a chip reads
              // `filters[key]` verbatim, so leading/trailing spaces typed
              // into either field would otherwise show up in its own label.
              onApply({
                ...draft,
                facilityQuery: draft.facilityQuery.trim(),
                physicianQuery: draft.physicianQuery.trim(),
              })
            }
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};
